// Layout.jsx
import React, { useEffect, useRef, useState } from "react";
import "./Layout.css";
import "./PortalUI.css";
import logoLight from "./assets/logo-light.svg";
import logoDark from "./assets/logo-dark.svg";
import { getCurrentUser, getHealth } from "./api";
import { getThemePreference, applyThemePreference } from "./theme";

const FRONTEND_UPDATE_CHECK_INTERVAL_MS = 60_000;
const FRONTEND_UPDATE_SUPPRESS_MS = 60 * 60 * 1000; // 1 hour
const FRONTEND_UPDATE_SUPPRESS_KEY = "frontend_update_toast_suppressed_until";

function extractBuildIdFromPath(pathValue) {
    const value = String(pathValue || "");
    if (!value) return "";
    const mainMatch = value.match(/main\.([a-z0-9]+)\.(?:js|css)/i);
    if (mainMatch?.[1]) return mainMatch[1];

    const chunkMatch = value.match(/(?:^|\/)(?:static\/)?(?:js|css)\/[^/]*\.([a-z0-9]{6,})\.(?:js|css)/i);
    if (chunkMatch?.[1]) return chunkMatch[1];

    return value;
}

function getCurrentBuildIdFromDom() {
    if (typeof document === "undefined") return "";
    const scripts = Array.from(document.querySelectorAll("script[src]"));
    for (const script of scripts) {
        const src = String(script.getAttribute("src") || "");
        if (!src) continue;
        const buildId = extractBuildIdFromPath(src);
        if (buildId) return buildId;
    }
    return "";
}

function extractBuildIdFromManifest(manifest) {
    if (!manifest || typeof manifest !== "object") return "";
    const files = manifest.files && typeof manifest.files === "object"
        ? manifest.files
        : null;
    const mainJs = files?.["main.js"] || files?.["main.js.map"] || "";
    const viaMain = extractBuildIdFromPath(mainJs);
    if (viaMain) return viaMain;

    const fileValues = files ? Object.values(files) : [];
    for (const value of fileValues) {
        const buildId = extractBuildIdFromPath(value);
        if (buildId) return buildId;
    }
    return "";
}

async function fetchLatestBuildId() {
    const manifestUrl = new URL(
        `${process.env.PUBLIC_URL || ""}/asset-manifest.json`,
        window.location.origin
    );
    manifestUrl.searchParams.set("t", String(Date.now()));
    const res = await fetch(manifestUrl.toString(), {
        cache: "no-store",
    });
    if (!res.ok) return "";
    const data = await res.json().catch(() => null);
    return extractBuildIdFromManifest(data);
}

/* ----------------------------------------
   SVG ICONS
---------------------------------------- */

const IconDashboard = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M17.1189 18C19.4623 16.4151 21 13.7779 21 10.785C21 5.9333 16.9704 2 12 2C7.02958 2 3 5.9333 3 10.785C3 13.7779 4.53771 16.4151 6.88113 18" />
        <circle cx="12" cy="11" r="1" />
        <path d="M12 16V22M10 20C10 18.8954 10.8954 18 12 18C13.1046 18 14 18.8954 14 20C14 21.1046 13.1046 22 12 22" />
    </svg>
);

const IconEpisodes = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M3 3V13.2C3 16 4 18 7.8 18H15" />
        <circle cx="18" cy="18" r="3" />
        <path d="M3 8H15" />
        <circle cx="18" cy="8" r="3" />
    </svg>
);

const IconPodcasts = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <circle cx="12" cy="12" r="10" />
        <circle cx="12" cy="12" r="4" />
    </svg>
);

const IconClients = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <circle cx="12" cy="7" r="4" />
        <path d="M4 21C4 17 8 15 12 15C16 15 20 17 20 21" />
    </svg>
);

const IconLeads = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M4 7h16M4 12h10M4 17h8" />
        <circle cx="18" cy="12" r="3" />
    </svg>
);

const IconMeetings = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
);

const IconSettings = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 
                 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 
                 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 
                 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 
                 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 
                 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 
                 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 
                 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 
                 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 
                 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 
                 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 
                 1.65 0 0 0-.33 1.82V9c0 .7.4 1.34 1.01 1.66.33.17.7.26 
                 1.09.26H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 
                 0-1.51 1z"
        />
    </svg>
);

const IconUsers = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <path d="M20 8v6M23 11h-6" />
    </svg>
);

const IconFiles = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M3 7a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
);

const IconLogout = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
    >
        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
        <polyline points="16 17 21 12 16 7" />
        <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
);

const IconToggle = ({ collapsed }) => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        {collapsed ? (
            <polyline points="8 4 16 12 8 20" />
        ) : (
            <polyline points="16 4 8 12 16 20" />
        )}
    </svg>
);

/* Simple hamburger icon for mobile */
const IconMenu = () => (
    <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="3" y1="6" x2="21" y2="6" />
        <line x1="3" y1="12" x2="21" y2="12" />
        <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
);

/* ----------------------------------------
   Layout Component
---------------------------------------- */

function getUserInitials(user) {
    if (!user) return "UP";

    const firstName = String(user.first_name || "").trim();
    const surname = String(user.surname || "").trim();
    if (firstName && surname) {
        return `${firstName[0]}${surname[0]}`.toUpperCase();
    }
    if (firstName) return firstName.slice(0, 2).toUpperCase();

    const fullName = String(user.name || "").trim();
    if (fullName) {
        const parts = fullName.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
            return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
        }
        return parts[0].slice(0, 2).toUpperCase();
    }

    const email = String(user.email || "").trim();
    if (email) return email.slice(0, 2).toUpperCase();

    const username = String(user.username || "").trim();
    if (username) return username.slice(0, 2).toUpperCase();
    return "UP";
}

function Layout({ children, onLogout, currentUser: currentUserProp = null }) {
    // Router hash (for active nav styling)
    const [currentHash, setCurrentHash] = useState(
        window.location.hash || "#/dashboard"
    );

    // Sidebar collapsed state (desktop)
    const [collapsed, setCollapsed] = useState(() => {
        const saved = localStorage.getItem("sidebarCollapsed");
        return saved === null ? true : saved === "true";
    });

    // Mobile slide-in sidebar open/closed
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

    // API status: "checking" | "ok" | "down"
    const [apiStatus, setApiStatus] = useState("checking");
    const [currentUser, setCurrentUser] = useState(currentUserProp);
    const [isUserResolved, setIsUserResolved] = useState(
        Boolean(currentUserProp)
    );
    const [isFrontendUpdateAvailable, setIsFrontendUpdateAvailable] = useState(false);
    const [latestFrontendBuildId, setLatestFrontendBuildId] = useState("");
    const currentFrontendBuildIdRef = useRef("");

    // Apply saved / system theme on mount
    useEffect(() => {
        const pref = getThemePreference();
        applyThemePreference(pref);
    }, []);

    // Persist sidebar state (desktop)
    useEffect(() => {
        localStorage.setItem("sidebarCollapsed", String(collapsed));
    }, [collapsed]);

    // Listen for hash changes
    useEffect(() => {
        const handleHash = () =>
            setCurrentHash(window.location.hash || "#/dashboard");
        window.addEventListener("hashchange", handleHash);
        return () => window.removeEventListener("hashchange", handleHash);
    }, []);

    // Close mobile sidebar when resizing back to desktop
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 900) {
                setIsMobileSidebarOpen(false);
            }
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // Poll API health
    useEffect(() => {
        let cancelled = false;

        async function checkHealth() {
            try {
                const result = await getHealth();
                if (cancelled) return;
                setApiStatus(result.ok ? "ok" : "down");
            } catch {
                if (!cancelled) setApiStatus("down");
            }
        }

        checkHealth(); // initial
        const intervalId = setInterval(checkHealth, 60_000); // every 60s

        return () => {
            cancelled = true;
            clearInterval(intervalId);
        };
    }, []);

    useEffect(() => {
        if (currentUserProp) {
            setCurrentUser(currentUserProp);
            setIsUserResolved(true);
            return;
        }

        let cancelled = false;

        async function loadCurrentUser() {
            try {
                const res = await getCurrentUser();
                if (cancelled) return;
                if (res?.ok && res.user) {
                    setCurrentUser(res.user);
                }
            } catch (err) {
                console.error("Failed to load current user in layout:", err);
            } finally {
                if (!cancelled) {
                    setIsUserResolved(true);
                }
            }
        }

        loadCurrentUser();
        return () => {
            cancelled = true;
        };
    }, [currentUserProp]);

    const toggleSidebar = () => {
        if (window.innerWidth <= 900) {
            // Mobile: slide drawer in/out
            setIsMobileSidebarOpen((prev) => !prev);
        } else {
            // Desktop: collapse / expand
            setCollapsed((prev) => !prev);
        }
    };

    const closeSidebar = () => {
        setIsMobileSidebarOpen(false);
    };

    const isClient = currentUser?.role === "client";
    const isAdmin = currentUser?.role === "admin";
    const canSeeFileExplorer = isAdmin || Boolean(currentUser?.has_file_browser_access);
    const userInitials = getUserInitials(currentUser);

    const navItems = !isUserResolved
        ? []
        : isClient
            ? [
                { href: "#/dashboard", label: "Dashboard", icon: <IconDashboard /> },
                { href: "#/episodes", label: "Episodes", icon: <IconEpisodes /> },
                ...(canSeeFileExplorer
                    ? [{ href: "#/files", label: "File Explorer", icon: <IconFiles /> }]
                    : []),
                { href: "#/settings", label: "Settings", icon: <IconSettings /> },
            ]
            : [
                { href: "#/dashboard", label: "Dashboard", icon: <IconDashboard /> },
                { href: "#/episodes", label: "Episodes", icon: <IconEpisodes /> },
                { href: "#/podcasts", label: "Podcasts", icon: <IconPodcasts /> },
                ...(canSeeFileExplorer
                    ? [{ href: "#/files", label: "File Explorer", icon: <IconFiles /> }]
                    : []),
                { href: "#/clients", label: "Clients", icon: <IconClients /> },
                { href: "#/leads", label: "Leads", icon: <IconLeads /> },
                { href: "#/meetings", label: "Meetings", icon: <IconMeetings /> },
                ...(isAdmin
                    ? [
                        {
                            href: "#/user-management",
                            label: "User Management",
                            icon: <IconUsers />,
                        },
                    ]
                    : []),
                { href: "#/settings", label: "Settings", icon: <IconSettings /> },
            ];

    const isActive = (href) => currentHash.startsWith(href);

    const handleNavClick = () => {
        // Close mobile drawer after navigation
        if (window.innerWidth <= 900) {
            setIsMobileSidebarOpen(false);
        }
    };

    // Badge label + classes based on API status
    const badgeLabel =
        apiStatus === "ok"
            ? "Operational"
            : apiStatus === "checking"
                ? "Checking…"
                : "API issues";

    const badgeClassName =
        "portal-topbar__badge " +
        (apiStatus === "ok"
            ? "portal-topbar__badge--ok"
            : apiStatus === "checking"
                ? "portal-topbar__badge--checking"
                : "portal-topbar__badge--error");

    const badgeDotStyle =
        apiStatus === "ok"
            ? { background: "#22c55e" } // green
            : apiStatus === "checking"
                ? { background: "#eab308" } // amber
                : { background: "#ef4444" }; // red

    useEffect(() => {
        currentFrontendBuildIdRef.current = getCurrentBuildIdFromDom();
    }, []);

    useEffect(() => {
        if (!isAdmin) {
            setIsFrontendUpdateAvailable(false);
            return;
        }

        let cancelled = false;
        let inFlight = false;

        const getSuppressedUntil = () =>
            Number(localStorage.getItem(FRONTEND_UPDATE_SUPPRESS_KEY) || 0);
        const isSuppressed = () => Date.now() < getSuppressedUntil();

        async function checkForFrontendUpdate() {
            if (cancelled || inFlight) return;
            inFlight = true;
            try {
                const latestBuildId = await fetchLatestBuildId();
                if (cancelled || !latestBuildId) return;

                setLatestFrontendBuildId(latestBuildId);
                if (!currentFrontendBuildIdRef.current) {
                    currentFrontendBuildIdRef.current = latestBuildId;
                    return;
                }

                const hasUpdate =
                    currentFrontendBuildIdRef.current &&
                    latestBuildId !== currentFrontendBuildIdRef.current;
                if (hasUpdate && !isSuppressed()) {
                    setIsFrontendUpdateAvailable(true);
                }
            } catch (err) {
                // Best-effort check; ignore transient failures.
            } finally {
                inFlight = false;
            }
        }

        checkForFrontendUpdate();
        const intervalId = setInterval(
            checkForFrontendUpdate,
            FRONTEND_UPDATE_CHECK_INTERVAL_MS
        );

        const handleVisibility = () => {
            if (document.visibilityState === "visible") {
                checkForFrontendUpdate();
            }
        };
        document.addEventListener("visibilitychange", handleVisibility);

        return () => {
            cancelled = true;
            clearInterval(intervalId);
            document.removeEventListener("visibilitychange", handleVisibility);
        };
    }, [isAdmin]);

    const handleRefreshForFrontendUpdate = () => {
        window.location.reload();
    };

    const handleDismissFrontendUpdateToast = () => {
        const suppressUntil = Date.now() + FRONTEND_UPDATE_SUPPRESS_MS;
        localStorage.setItem(FRONTEND_UPDATE_SUPPRESS_KEY, String(suppressUntil));
        setIsFrontendUpdateAvailable(false);
    };

    return (
        <div className="portal-shell">
            {/* Sidebar */}
            <aside
                className={
                    "portal-sidebar" +
                    (collapsed ? " portal-sidebar--collapsed" : "") +
                    (isMobileSidebarOpen ? " portal-sidebar--mobile-open" : "")
                }
            >
                {/* Brand */}
                <div className="portal-sidebar__brand">
                    <div className="portal-sidebar__brand-text portal-sidebar__fade">
                        <div className="portal-sidebar__brand-title">
                            Urban Podcasts
                        </div>
                        <div className="portal-sidebar__brand-subtitle">
                            Production Portal
                        </div>
                    </div>
                </div>

                {/* Nav */}
                <nav className="portal-sidebar__nav">
                    {navItems.map((item) => (
                        <a
                            key={item.href}
                            href={item.href}
                            onClick={handleNavClick}
                            className={
                                "portal-sidebar__link" +
                                (isActive(item.href)
                                    ? " portal-sidebar__link--active"
                                    : "")
                            }
                        >
                            <span className="portal-sidebar__link-icon">
                                {item.icon}
                            </span>

                            <span className="portal-sidebar__link-label portal-sidebar__fade">
                                {item.label}
                            </span>
                        </a>
                    ))}
                </nav>

                {/* Bottom Toggle Button (desktop only, hidden via CSS on mobile) */}
                <button
                    type="button"
                    className="portal-sidebar__toggle"
                    onClick={toggleSidebar}
                >
                    <IconToggle collapsed={collapsed} />
                </button>
            </aside>

            {/* 🔹 Mobile overlay: tap anywhere to close sidebar */}
            {isMobileSidebarOpen && (
                <div
                    className="portal-sidebar-overlay"
                    onClick={closeSidebar}
                />
            )}

            {/* Main Section */}
            <main className="portal-main">
                <header className="portal-topbar">
                    <div className="portal-topbar__title">
                        {/* Mobile hamburger */}
                        <button
                            type="button"
                            className="portal-topbar__menu-btn"
                            onClick={toggleSidebar}
                        >
                            <IconMenu />
                        </button>

                        {/* Title text (hidden on mobile via CSS) */}
                        <div className="portal-topbar__title-text">
                            <div className="portal-topbar__brand" aria-label="Urban Podcasts Portal">
                                <img
                                    src={logoDark}
                                    alt="Urban Podcasts Portal"
                                    className="portal-topbar__brand-logo portal-topbar__brand-logo--dark"
                                />
                                <img
                                    src={logoLight}
                                    alt="Urban Podcasts Portal"
                                    className="portal-topbar__brand-logo portal-topbar__brand-logo--light"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="portal-topbar__meta">
                        {isAdmin && (
                            <div className={badgeClassName}>
                                <span
                                    className="portal-topbar__badge-dot"
                                    style={badgeDotStyle}
                                />
                                <span>{badgeLabel}</span>
                            </div>
                        )}

                        <div className="portal-topbar__user">
                            <div className="portal-topbar__avatar">{userInitials}</div>
                            <div className="portal-topbar__user-text">
                                <span className="portal-topbar__user-name">
                                    {currentUser?.first_name?.trim() ||
                                        currentUser?.name?.trim() ||
                                        currentUser?.email ||
                                        currentUser?.username ||
                                        "Team"}
                                </span>
                                {isAdmin && (
                                    <span className="portal-topbar__user-role">
                                        Admin
                                    </span>
                                )}
                            </div>
                        </div>

                        <button
                            type="button"
                            className="portal-topbar__logout"
                            onClick={onLogout}
                        >
                            <IconLogout />
                            <span>Logout</span>
                        </button>
                    </div>
                </header>

                <div className="portal-content">{children}</div>
            </main>

            {isAdmin && isFrontendUpdateAvailable && (
                <div className="toast-container">
                    <div className="toast toast--info">
                        <span className="toast__message">
                            A new version of the portal is available.
                        </span>
                        <div className="toast__actions">
                            <button
                                type="button"
                                className="toast__action-btn"
                                onClick={handleRefreshForFrontendUpdate}
                                title={
                                    latestFrontendBuildId
                                        ? `Latest build: ${latestFrontendBuildId}`
                                        : "Refresh now"
                                }
                            >
                                Refresh now
                            </button>
                            <button
                                type="button"
                                className="toast__close"
                                onClick={handleDismissFrontendUpdateToast}
                                aria-label="Dismiss update notification"
                            >
                                ×
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Layout;
